"use client";

import { c as _c } from "react/compiler-runtime";
import Tooltip from "@/components/ui/tooltip";
import { formatDate } from "@/lib/format-data";
import { type BiomarkerStatus, ageOpacity, currentRange, formatRange, positionPercent, rangeDomain, resultStatus } from "@/lib/lab-biomarker-range";
import { cx } from "@/lib/tailwind-utils";
import type { LabBiomarkerHistory, LabBiomarkerHistoryResult } from "@/types/lab";
const DOT_COLOR: Record<BiomarkerStatus, string> = {
  normal: "bg-status-success-text",
  out: "bg-status-warning-text",
  critical: "bg-status-error-text",
  unknown: "bg-foreground-secondary"
};
const TEXT_COLOR: Record<BiomarkerStatus, string> = {
  normal: "text-foreground",
  out: "text-status-warning-text",
  critical: "text-status-error-text",
  unknown: "text-foreground"
};
function displayValue(result: LabBiomarkerHistoryResult): string {
  return result.valueNumeric != null ? String(result.valueNumeric) : result.valueDisplay ?? "—";
}
function shortDate(result: LabBiomarkerHistoryResult): string {
  return formatDate(result.collectedAt);
}
interface Props {
  history: LabBiomarkerHistory;
  onOpenResult: (result: LabBiomarkerHistoryResult) => void;
}

/**
 * One marker: the latest value against the previous one, and a bar with the
 * reference band and a dot per result. Only the newest dot is colored; older
 * ones are grey and fade with age. Click a dot to open its lab.
 */
export default function BiomarkerRangeRow(t0) {
  const $ = _c(53);
  const {
    history,
    onOpenResult
  } = t0;
  const [latest, previous] = history.results;
  let t1;
  if ($[0] !== latest) {
    t1 = resultStatus(latest);
    $[0] = latest;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const status = t1;
  const unit = history.unit ? ` ${history.unit}` : "";
  let rangeLabel;
  let rangeResult;
  let t2;
  let t3;
  let t4;
  let t5;
  if ($[2] !== history || $[3] !== latest || $[4] !== onOpenResult || $[5] !== previous || $[6] !== status || $[7] !== unit) {
    rangeResult = currentRange(history);
    rangeLabel = formatRange(rangeResult ?? latest);
    const numeric = history.results.filter(_temp);
    const values = numeric.map(_temp2);
    const range = {
      min: rangeResult?.minRangeValue,
      max: rangeResult?.maxRangeValue
    };
    const domain = values.length > 0 ? rangeDomain(values, range) : null;
    t2 = "rounded-md px-2 py-1.5";
    let t6;
    if ($[14] !== history.name) {
      t6 = <span className="truncate text-body font-medium text-foreground">{history.name}</span>;
      $[14] = history.name;
      $[15] = t6;
    } else {
      t6 = $[15];
    }
    const t7 = TEXT_COLOR[status];
    let t8;
    if ($[16] !== t7) {
      t8 = cx("shrink-0 text-body font-medium hover:underline", t7);
      $[16] = t7;
      $[17] = t8;
    } else {
      t8 = $[17];
    }
    let t9;
    if ($[18] !== latest || $[19] !== onOpenResult) {
      t9 = () => onOpenResult(latest);
      $[18] = latest;
      $[19] = onOpenResult;
      $[20] = t9;
    } else {
      t9 = $[20];
    }
    let t10;
    if ($[21] !== latest) {
      t10 = displayValue(latest);
      $[21] = latest;
      $[22] = t10;
    } else {
      t10 = $[22];
    }
    let t11;
    if ($[23] !== unit) {
      t11 = <span className="text-caption font-normal text-foreground-secondary">{unit}</span>;
      $[23] = unit;
      $[24] = t11;
    } else {
      t11 = $[24];
    }
    let t12;
    if ($[25] !== t10 || $[26] !== t11 || $[27] !== t8 || $[28] !== t9) {
      t12 = <button type="button" className={t8} onClick={t9}>{t10}{t11}</button>;
      $[25] = t10;
      $[26] = t11;
      $[27] = t8;
      $[28] = t9;
      $[29] = t12;
    } else {
      t12 = $[29];
    }
    if ($[30] !== t12 || $[31] !== t6) {
      t3 = <div className="flex items-baseline justify-between gap-2">{t6}{t12}</div>;
      $[30] = t12;
      $[31] = t6;
      $[32] = t3;
    } else {
      t3 = $[32];
    }
    let t13;
    if ($[33] !== latest) {
      t13 = shortDate(latest);
      $[33] = latest;
      $[34] = t13;
    } else {
      t13 = $[34];
    }
    let t14;
    if ($[35] !== onOpenResult || $[36] !== previous) {
      t14 = previous && <>{" \xB7 prev "}<button type="button" className="hover:underline" onClick={() => onOpenResult(previous)}>{displayValue(previous)} on {shortDate(previous)}</button></>;
      $[35] = onOpenResult;
      $[36] = previous;
      $[37] = t14;
    } else {
      t14 = $[37];
    }
    if ($[38] !== t13 || $[39] !== t14) {
      t4 = <p className="truncate text-caption text-foreground-secondary">{t13}{t14}</p>;
      $[38] = t13;
      $[39] = t14;
      $[40] = t4;
    } else {
      t4 = $[40];
    }
    t5 = domain && <div className="relative mb-1 mt-5 h-3"><div className="absolute inset-x-0 top-1/2 h-1 -translate-y-1/2 rounded-full bg-muted" />{(range.min != null || range.max != null) && <div className="absolute top-1/2 h-1 -translate-y-1/2 rounded-full bg-status-success-text/40" style={{
        left: `${range.min != null ? positionPercent(range.min, domain) : 0}%`,
        right: `${100 - (range.max != null ? positionPercent(range.max, domain) : 100)}%`
      }} />}{[...numeric].reverse().map(result => {
        const isLatest = result === latest;
        const left = positionPercent(result.valueNumeric as number, domain);
        return <div key={result.id} className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2" style={{
          left: `${left}%`
        }}>{isLatest && <span className="pointer-events-none absolute bottom-full left-1/2 mb-1 -translate-x-1/2 whitespace-nowrap text-[10px] leading-none text-foreground-secondary">{shortDate(result)}</span>}<Tooltip triggerAsChild={true} content={`${displayValue(result)}${unit} · ${shortDate(result)}${result.labName ? ` · ${result.labName}` : ""}`}><button type="button" aria-label={`${history.name} ${displayValue(result)}${unit} on ${shortDate(result)}`} className={cx("block rounded-full ring-background transition-transform hover:scale-125", isLatest ? cx("size-3 ring-2", DOT_COLOR[resultStatus(result)]) : "size-2.5 bg-foreground-secondary ring-1")} style={isLatest ? undefined : {
              opacity: ageOpacity(result.collectedAt, latest.collectedAt)
            }} onClick={() => onOpenResult(result)} /></Tooltip></div>;
      })}</div>;
    $[2] = history;
    $[3] = latest;
    $[4] = onOpenResult;
    $[5] = previous;
    $[6] = status;
    $[7] = unit;
    $[8] = rangeLabel;
    $[9] = rangeResult;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
  } else {
    rangeLabel = $[8];
    rangeResult = $[9];
    t2 = $[10];
    t3 = $[11];
    t4 = $[12];
    t5 = $[13];
  }
  let t6;
  if ($[41] !== rangeLabel || $[42] !== rangeResult?.rangeSource || $[43] !== unit) {
    t6 = rangeLabel ? <>Ref {rangeLabel}{unit}{rangeResult?.rangeSource === "clinic" && " (clinic)"}</> : "No reference range";
    $[41] = rangeLabel;
    $[42] = rangeResult?.rangeSource;
    $[43] = unit;
    $[44] = t6;
  } else {
    t6 = $[44];
  }
  let t7;
  if ($[45] !== t6) {
    t7 = <p className="text-caption text-foreground-secondary">{t6}</p>;
    $[45] = t6;
    $[46] = t7;
  } else {
    t7 = $[46];
  }
  let t8;
  if ($[47] !== t2 || $[48] !== t3 || $[49] !== t4 || $[50] !== t5 || $[51] !== t7) {
    t8 = <div className={t2}>{t3}{t4}{t5}{t7}</div>;
    $[47] = t2;
    $[48] = t3;
    $[49] = t4;
    $[50] = t5;
    $[51] = t7;
    $[52] = t8;
  } else {
    t8 = $[52];
  }
  return t8;
}
function _temp2(r_0) {
  return r_0.valueNumeric as number;
}
function _temp(r) {
  return r.valueNumeric != null;
}